import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { ArrowLeft, ClipboardCheck, ChevronRight, Wallet, PiggyBank, CreditCard, Briefcase, FileSearch, FileUser, Sparkles, TrendingUp } from 'lucide-react';

const QUESTIONS = [
  {
    id: 1,
    question: 'Hur bra koll har du på dina inkomster och utgifter?',
    options: [
      { text: 'Jag har full koll och budgeterar varje månad', score: { budget: 0 } },
      { text: 'Jag har hyfsad koll men ingen strikt budget', score: { budget: 1 } },
      { text: 'Jag vet ungefär vad jag tjänar men inte vart pengarna går', score: { budget: 2 } },
      { text: 'Jag har ingen aning och lever månad till månad', score: { budget: 3 } },
    ]
  },
  {
    id: 2,
    question: 'Hur ser din sparandesituation ut?',
    options: [
      { text: 'Jag sparar regelbundet och har nått mina mål', score: { savings: 0 } },
      { text: 'Jag sparar ibland men har inga tydliga mål', score: { savings: 1 } },
      { text: 'Jag vill spara men det blir sällan något över', score: { savings: 2 } },
      { text: 'Jag har inga besparingar alls', score: { savings: 3 } },
    ]
  },
  {
    id: 3,
    question: 'Har du en buffert för oväntade utgifter?',
    options: [
      { text: 'Ja, minst 3 månaders utgifter', score: { savings: 0 } },
      { text: 'Ja, men mindre än 3 månader', score: { savings: 1 } },
      { text: 'Jag har lite sparat men det räcker inte långt', score: { savings: 2 } },
      { text: 'Nej, jag har ingen buffert', score: { savings: 3, budget: 1 } },
    ]
  },
  {
    id: 4,
    question: 'Hur är din skuldsituation?',
    options: [
      { text: 'Inga skulder (förutom ev. bolån)', score: { debts: 0 } },
      { text: 'Jag har skulder men betalar av enligt plan', score: { debts: 1 } },
      { text: 'Jag har skulder och kämpar med betalningarna', score: { debts: 2 } },
      { text: 'Jag har skulder som växer och vet inte hur jag ska hantera dem', score: { debts: 3 } },
    ]
  },
  {
    id: 5,
    question: 'Hur känner du inför ditt nuvarande jobb?',
    options: [
      { text: 'Jag trivs och ser en framtid här', score: { career: 0 } },
      { text: 'Det är okej men jag vill utvecklas mer', score: { career: 1 } },
      { text: 'Jag trivs inte och funderar på att byta', score: { career: 2, jobsearch: 1 } },
      { text: 'Jag är arbetslös och söker jobb', score: { jobsearch: 3 } },
    ]
  },
  {
    id: 6,
    question: 'Hur är din lön jämfört med marknaden?',
    options: [
      { text: 'Jag är nöjd och vet att jag ligger bra till', score: { career: 0 } },
      { text: 'Jag vet inte riktigt vad jag borde tjäna', score: { career: 1 } },
      { text: 'Jag tror att jag är underbetalad', score: { career: 2 } },
      { text: 'Jag vågar inte förhandla lön', score: { career: 2, cvhelp: 1 } },
    ]
  },
  {
    id: 7,
    question: 'När uppdaterade du senast ditt CV?',
    options: [
      { text: 'Det senaste året och det är professionellt', score: { cvhelp: 0 } },
      { text: 'För 1-3 år sedan', score: { cvhelp: 1 } },
      { text: 'Mer än 3 år sedan', score: { cvhelp: 2 } },
      { text: 'Jag har inget CV eller vet inte hur man skriver ett', score: { cvhelp: 3 } },
    ]
  },
  {
    id: 8,
    question: 'Hur planerar du för framtiden ekonomiskt?',
    options: [
      { text: 'Jag har en tydlig plan för pension och långsiktigt sparande', score: { savings: 0 } },
      { text: 'Jag tänker på det ibland men har ingen plan', score: { savings: 1, budget: 1 } },
      { text: 'Jag lever i nuet och tänker inte så mycket på framtiden', score: { savings: 2, budget: 1 } },
      { text: 'Jag oroar mig för framtiden men vet inte var jag ska börja', score: { savings: 2, budget: 2 } },
    ]
  },
];

const RESULTS_MAP = {
  budget: {
    title: 'Budget',
    icon: Wallet,
    path: '/finance',
    color: 'bg-teal-100 text-teal-600',
    description: 'Få koll på inkomster och utgifter'
  },
  savings: {
    title: 'Sparmål',
    icon: PiggyBank,
    path: '/savings',
    color: 'bg-green-100 text-green-600',
    description: 'Sätt upp mål och bygg en buffert'
  },
  debts: {
    title: 'Skulder',
    icon: CreditCard,
    path: '/debts',
    color: 'bg-red-100 text-red-600',
    description: 'Få överblick och en avbetalningsplan'
  },
  career: {
    title: 'Karriär',
    icon: Briefcase,
    path: '/career',
    color: 'bg-purple-100 text-purple-600',
    description: 'Utveckla din karriär och förhandla lön'
  },
  jobsearch: {
    title: 'Jobbsökning',
    icon: FileSearch,
    path: '/job-search',
    color: 'bg-blue-100 text-blue-600',
    description: 'Hitta nytt jobb och spåra ansökningar'
  },
  cvhelp: {
    title: 'CV-hjälp',
    icon: FileUser,
    path: '/cv-help',
    color: 'bg-amber-100 text-amber-600',
    description: 'Skapa ett professionellt CV'
  },
};

export default function EconomyCheckPage() {
  const navigate = useNavigate();
  const { hasAccess } = useAuth();
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [scores, setScores] = useState({
    budget: 0,
    savings: 0,
    debts: 0,
    career: 0,
    jobsearch: 0,
    cvhelp: 0,
  });
  const [showResults, setShowResults] = useState(false);

  if (!hasAccess) {
    return (
      <div className="min-h-screen bg-background" data-testid="economy-check-page">
        <header className="border-b border-border/50 bg-card/50 backdrop-blur-sm sticky top-0 z-50">
          <div className="max-w-4xl mx-auto px-6 py-4 flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/category/economy')} className="rounded-full">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="font-heading text-xl font-light">Ekonomihälso-check</h1>
          </div>
        </header>
        <main className="max-w-4xl mx-auto px-6 py-12 text-center">
          <p className="text-muted-foreground">Uppgradera för att få tillgång till testet.</p>
          <Button onClick={() => navigate('/subscription')} className="rounded-full mt-4">Uppgradera nu</Button>
        </main>
      </div>
    );
  }

  const handleAnswer = (option) => {
    const newAnswers = { ...answers, [currentQuestion]: option };
    setAnswers(newAnswers);

    const newScores = { ...scores };
    Object.entries(option.score).forEach(([key, value]) => {
      newScores[key] = (newScores[key] || 0) + value;
    });
    setScores(newScores);

    if (currentQuestion < QUESTIONS.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    } else {
      setShowResults(true);
    }
  };

  const getTopResults = () => {
    const sorted = Object.entries(scores)
      .filter(([_, score]) => score > 0)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 3);
    
    return sorted.map(([key]) => RESULTS_MAP[key]);
  };

  const getOverallHealth = () => {
    const totalScore = Object.values(scores).reduce((sum, s) => sum + s, 0);
    const maxPossible = QUESTIONS.length * 3;
    const percentage = 100 - Math.round((totalScore / maxPossible) * 100);
    
    if (percentage >= 80) return { label: 'Utmärkt', color: 'text-green-600', emoji: '🌟' };
    if (percentage >= 60) return { label: 'Bra', color: 'text-teal-600', emoji: '👍' };
    if (percentage >= 40) return { label: 'Förbättringspotential', color: 'text-amber-600', emoji: '💪' };
    return { label: 'Behöver uppmärksamhet', color: 'text-red-600', emoji: '⚠️' };
  };

  const resetTest = () => {
    setCurrentQuestion(0);
    setAnswers({});
    setScores({
      budget: 0,
      savings: 0,
      debts: 0,
      career: 0,
      jobsearch: 0,
      cvhelp: 0,
    });
    setShowResults(false);
  };

  // Results view
  if (showResults) {
    const topResults = getTopResults();
    const health = getOverallHealth();
    const hasDebtIssue = scores.debts >= 3;

    return (
      <div className="min-h-screen bg-background" data-testid="economy-check-page">
        <header className="border-b border-border/50 bg-card/50 backdrop-blur-sm sticky top-0 z-50">
          <div className="max-w-4xl mx-auto px-6 py-4 flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/category/economy')} className="rounded-full">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="font-heading text-xl font-light">Ditt resultat</h1>
          </div>
        </header>

        <main className="max-w-4xl mx-auto px-6 py-8 space-y-6">
          <div className="text-center space-y-3">
            <TrendingUp className="w-12 h-12 mx-auto text-teal-500" />
            <h2 className="font-heading text-2xl font-semibold">Din ekonomihälso-check</h2>
            <div className={`text-lg font-semibold ${health.color}`}>
              {health.emoji} {health.label}
            </div>
          </div>

          {/* Urgent warning if debt score is high */}
          {hasDebtIssue && (
            <div className="bg-red-50 rounded-2xl p-4 border border-red-200">
              <p className="text-red-800 text-sm font-semibold mb-2">⚠️ Viktigt om skulder</p>
              <p className="text-red-700 text-sm">
                Dina svar tyder på att du kan ha problem med skulder. Det är viktigt att ta tag i detta! 
                Vi rekommenderar att du börjar med vår skuldsida för att få en överblick och skapa en avbetalningsplan.
              </p>
              <p className="text-red-700 text-sm mt-2">
                Vid allvarliga skuldproblem: Kontakta budget- och skuldrådgivning i din kommun (gratis).
              </p>
            </div>
          )}

          {/* Top recommendations */}
          <div className="space-y-3">
            <h3 className="font-semibold text-lg">Fokusera på dessa områden</h3>
            {topResults.length > 0 ? (
              topResults.map((result, index) => (
                <button
                  key={result.path}
                  onClick={() => navigate(result.path)}
                  className="w-full bg-white p-5 rounded-2xl border border-border/30 text-left hover:shadow-md transition-all group"
                >
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-3">
                      <span className="w-8 h-8 rounded-full bg-teal-500/10 flex items-center justify-center text-teal-600 font-semibold">
                        {index + 1}
                      </span>
                      <div className={`w-12 h-12 rounded-xl ${result.color} flex items-center justify-center`}>
                        <result.icon className="w-6 h-6" />
                      </div>
                    </div>
                    <div className="flex-1">
                      <h4 className="font-semibold">{result.title}</h4>
                      <p className="text-sm text-muted-foreground">{result.description}</p>
                    </div>
                    <ChevronRight className="w-5 h-5 text-muted-foreground group-hover:text-primary" />
                  </div>
                </button>
              ))
            ) : (
              <div className="bg-green-50 rounded-2xl p-6 text-center">
                <Sparkles className="w-10 h-10 mx-auto text-green-500 mb-3" />
                <h4 className="font-semibold text-green-800">Imponerande!</h4>
                <p className="text-green-700 text-sm mt-1">
                  Du verkar ha bra koll på din ekonomi och karriär. Fortsätt så!
                </p>
              </div>
            )}
          </div>

          {/* All areas */}
          <div className="space-y-3">
            <h3 className="font-semibold text-lg">Alla ekonomiverktyg</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {Object.values(RESULTS_MAP).map((result) => (
                <button
                  key={result.path}
                  onClick={() => navigate(result.path)}
                  className="bg-white p-4 rounded-xl border border-border/30 text-center hover:shadow-md transition-all group"
                >
                  <div className={`w-10 h-10 mx-auto rounded-lg ${result.color} flex items-center justify-center mb-2`}>
                    <result.icon className="w-5 h-5" />
                  </div>
                  <p className="text-sm font-medium">{result.title}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-3 pt-4">
            <Button onClick={resetTest} variant="outline" className="flex-1 rounded-full">
              Gör testet igen
            </Button>
            <Button onClick={() => navigate('/category/economy')} className="flex-1 rounded-full">
              Tillbaka till startsidan
            </Button>
          </div>

          {/* Tips */}
          <div className="bg-teal-50 rounded-2xl p-4 border border-teal-100">
            <p className="text-teal-800 text-sm">
              💡 <strong>Tips:</strong> Börja med det som känns mest angeläget. Små steg varje dag leder till stora förändringar över tid!
            </p>
          </div>

          
        </main>
      </div>
    );
  }

  // Question view
  const question = QUESTIONS[currentQuestion];
  const progress = ((currentQuestion + 1) / QUESTIONS.length) * 100;

  return (
    <div className="min-h-screen bg-background" data-testid="economy-check-page">
      <header className="border-b border-border/50 bg-card/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/category/economy')} className="rounded-full">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <h1 className="font-heading text-xl font-light">Ekonomihälso-check</h1>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-6 py-8 space-y-6">
        {/* Progress */}
        <div className="space-y-2">
          <div className="flex justify-between text-sm text-muted-foreground">
            <span>Fråga {currentQuestion + 1} av {QUESTIONS.length}</span>
            <span>{Math.round(progress)}%</span>
          </div>
          <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
            <div 
              className="h-full bg-teal-500 transition-all duration-300 rounded-full"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        {/* Question */}
        <div className="bg-white rounded-2xl p-8 border border-border/30">
          <h2 className="font-heading text-xl font-semibold mb-6 text-center">
            {question.question}
          </h2>

          <div className="space-y-3">
            {question.options.map((option, index) => (
              <button
                key={index}
                onClick={() => handleAnswer(option)}
                className="w-full p-4 rounded-xl border border-border/50 text-left hover:border-teal-500 hover:bg-teal-50 transition-all"
              >
                <span className="text-foreground">{option.text}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Info */}
        <p className="text-xs text-muted-foreground text-center">
          Dina svar sparas inte och är helt anonyma. Testet är endast vägledande.
        </p>
      </main>
    </div>
  );
}
